// src/components/auth/ProtectedRoute.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoadingSpinner } from '../ui/LoadingSpinner';

/**
 * Enhanced ProtectedRoute component with role and permission-based access control
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if access is granted
 * @param {string} [props.requiredRole] - Minimum required role (user, admin, super_admin)
 * @param {string} [props.requiredPermission] - Single required permission
 * @param {string[]} [props.requiredPermissions] - Array of required permissions (user must have ALL)
 * @param {string[]} [props.requiredAnyPermissions] - Array of permissions (user must have ANY)
 * @param {string} [props.redirectTo] - Custom redirect path for unauthorized access
 * @param {React.ReactNode} [props.fallback] - Custom fallback component for unauthorized access
 * @param {boolean} [props.showUnauthorized] - Whether to show unauthorized page instead of redirecting
 */
export const ProtectedRoute = ({
  children,
  requiredRole,
  requiredPermission,
  requiredPermissions = [],
  requiredAnyPermissions = [],
  redirectTo = '/unauthorized',
  fallback = null,
  showUnauthorized = false
}) => {
  const {
    isAuthenticated,
    isLoading,
    hasRole,
    hasMinimumRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessRoute
  } = useAuth();
  
  const location = useLocation();

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole) {
    const hasRequiredRole = hasMinimumRole(requiredRole);
    if (!hasRequiredRole) {
      if (fallback) return fallback;
      if (showUnauthorized) return <Navigate to="/unauthorized" replace />;
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check single permission
  if (requiredPermission) {
    const hasRequiredPermission = hasPermission(requiredPermission);
    if (!hasRequiredPermission) {
      if (fallback) return fallback;
      if (showUnauthorized) return <Navigate to="/unauthorized" replace />;
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check multiple permissions (user must have ALL)
  if (requiredPermissions.length > 0) {
    const hasAllRequiredPermissions = hasAllPermissions(requiredPermissions);
    if (!hasAllRequiredPermissions) {
      if (fallback) return fallback;
      if (showUnauthorized) return <Navigate to="/unauthorized" replace />;
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check any permissions (user must have at least ONE)
  if (requiredAnyPermissions.length > 0) {
    const hasAnyRequiredPermission = hasAnyPermission(requiredAnyPermissions);
    if (!hasAnyRequiredPermission) {
      if (fallback) return fallback;
      if (showUnauthorized) return <Navigate to="/unauthorized" replace />;
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check route-based permissions using existing route mapping
  const currentPath = location.pathname;
  const canAccess = canAccessRoute(currentPath);
  if (!canAccess) {
    if (fallback) return fallback;
    if (showUnauthorized) return <Navigate to="/unauthorized" replace />;
    return <Navigate to={redirectTo} replace />;
  }

  // All checks passed, render children
  return children;
};

/**
 * Convenience component for admin-only routes
 */
export const AdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute
      requiredRole="admin"
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * Convenience component for super admin-only routes
 */
export const SuperAdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute
      requiredRole="super_admin"
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * Convenience component for user management routes
 */
export const UserManagementRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute
      requiredAnyPermissions={['users_view', 'users_manage']}
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * Convenience component for car management routes
 */
export const CarManagementRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute
      requiredAnyPermissions={['cars_view', 'cars_manage']}
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * Convenience component for trip management routes
 */
export const TripManagementRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute
      requiredAnyPermissions={['trips_view', 'trips_manage']}
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};

/**
 * Convenience component for driver management routes
 */
export const DriverManagementRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute
      requiredAnyPermissions={['drivers_view', 'drivers_manage']}
      {...props}
    >
      {children}
    </ProtectedRoute>
  );
};



/**
 * Public route component that redirects authenticated users to dashboard
 */
export const PublicRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * Higher-order component for protecting components with permissions
 */
export const withPermission = (Component, permission) => {
  return function PermissionWrappedComponent(props) {
    return (
      <ProtectedRoute requiredPermission={permission}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

/**
 * Higher-order component for protecting components with roles
 */
export const withRole = (Component, role) => {
  return function RoleWrappedComponent(props) {
    return (
      <ProtectedRoute requiredRole={role}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};
